import { Box, BoxProps } from "@mui/material";
import theme from "theme";
import React, { ComponentType } from "react";

/* NOTE: Flex Container */
type FlexContainerType = "row" | "column" | "none";
type FlexContainerProps = {
  boxRef?: React.RefObject<HTMLDivElement>;
  section?: string;
  fullWidth?: boolean;
  fullHeight?: boolean;
  children: React.ReactNode;
  type?: FlexContainerType;
} & BoxProps;

export const FlexContainer = ({
  section,
  children,
  fullWidth,
  fullHeight,
  type = "row",
  ...props
}: FlexContainerProps) => {
  const { sx, ...rest } = props;
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        width: fullWidth ? "100%" : "fit-content",
        height: fullHeight ? "100%" : "fit-content",
        ...props.sx,
      }}
      {...rest}>
      {children}
    </Box>
  );
};

export const withFlexContainer = <P extends BoxProps>(
  Wrapped: ComponentType<P>,
  type: FlexContainerType = "none",
) => {
  return ({
    fullWidth = false,
    fullHeight = false,
    sx,
    boxRef,
    children,
    ...props
  }: P & FlexContainerProps) => {
    return (
      <Wrapped
        ref={boxRef}
        sx={{
          display: type === "none" ? "block" : "flex",
          flexDirection: type === "none" ? "row" : type,
          alignItems: "center",
          justifyContent: "center",
          width: fullWidth ? "100%" : "fit-content",
          height: fullHeight ? "100%" : "fit-content",
          ...sx,
        }}
        {...(props as P)}>
        {children}
      </Wrapped>
    );
  };
};

export const Row = withFlexContainer(Box, "row");
export const Col = withFlexContainer(Box, "column");

/* NOTE: Card Component */
type CardType = "light" | "dark" | "silver" | "black";

const _2DCardConfigs: Record<
  CardType,
  { backgroundColor: string; border: string; color?: string }
> = {
  light: {
    backgroundColor: theme.palette.surface.light,
    border: `1px solid ${theme.palette.border.dark}`,
    color: theme.palette.text["btn-dark"],
  },
  dark: {
    backgroundColor: theme.palette.surface.container,
    border: `1px solid ${theme.palette.border.dark}`,
  },
  silver: {
    backgroundColor: theme.palette.surface.silver,
    border: `1px solid ${theme.palette.border.dark}`,
  },
  black: {
    backgroundColor: theme.palette.surface.bg,
    border: `1px solid ${theme.palette.border.light}`,
  },
};

export interface CardContainerProps extends FlexContainerProps {
  cardType?: CardType;
}

export const withCard = <P extends BoxProps>(Wrapped: ComponentType<P>) => {
  return ({
    cardType = "silver",
    fullWidth = true,
    fullHeight = false,
    sx,
    children,
    ...props
  }: P & CardContainerProps) => {
    const cardSx = {
      display: "flex",
      width: fullWidth ? "100%" : "fit-content",
      height: fullHeight ? "100%" : "fit-content",
      borderRadius: "16px",
      ..._2DCardConfigs[cardType],
      ...(sx || {}),
    };
    return (
      <Wrapped sx={cardSx} {...(props as P)}>
        {children}
      </Wrapped>
    );
  };
};

const _3DCardConfigs: Record<
  CardType,
  { backgroundColor: string; border: string; color?: string }
> = {
  light: {
    backgroundColor: theme.palette.surface.light,
    border: `1px solid ${theme.palette.border.dark}`,
    color: theme.palette.text["btn-dark"],
  },
  dark: {
    backgroundColor: theme.palette.surface.container,
    border: `1px solid ${theme.palette.border.dark}`,
  },
  silver: {
    backgroundColor: theme.palette.surface.silver,
    border: `1px solid ${theme.palette.border.dark}`,
  },
  black: {
    backgroundColor: theme.palette.surface.bg,
    border: `1px solid ${theme.palette.border.light}`,
  },
};

export const withThreeDCard = <P extends BoxProps>(Wrapped: ComponentType<P>) => {
  return ({
    cardType = "silver",
    fullWidth = true,
    fullHeight = false,
    sx,
    children,
    type = "none",
    ...props
  }: P & CardContainerProps) => {
    const cardSx = {
      backgroundColor: theme.palette.surface["container-bg"],
      borderRadius: "24px",
      padding: "0 4px 10px",
      position: "relative",
      height: fullHeight ? "100%" : "fit-content",
      width: fullWidth ? "100%" : "fit-content",
      overflow: "auto",
      "&:after": {
        content: '""',
        position: "absolute",
        left: "24px",
        right: "24px",
        height: "20px",
        bottom: 0,
        borderLeft: `1px solid ${theme.palette.border.dark}`,
        borderRight: `1px solid ${theme.palette.border.dark}`,
      },
    };
    return (
      <Wrapped sx={cardSx} {...(props as P)}>
        <Row
          sx={{
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "24px",
            marginTop: "-1px",
            position: "relative",
            height: fullHeight ? "100%" : "fit-content",
            overflow: "auto",
            zIndex: 1,
            width: fullWidth ? "100%" : "fit-content",
            ..._3DCardConfigs[cardType],
            ...(sx || {}),
          }}>
          {children}
        </Row>
      </Wrapped>
    );
  };
};

export const Card = {} as {
  _2D: any;
  _3D: any;
};

Card["_2D"] = withCard(FlexContainer);
Card["_3D"] = withThreeDCard(FlexContainer);
